// MoveRecordHouseholdModal (P1-core)
//
// Moves a single household-scoped record — an opportunity ("deal"),
// transaction (Processing), service record (policy/loan/case/listing), or
// rhythm instance (Ongoing Care plan) — onto a different household.
//
// Why it exists: moving a CONTACT out of a household never brought the
// deals with it, and there was previously no way to move the deals either,
// so a split stranded them on the old household. This is the missing "send
// this deal to the right household" action. It's fully reversible — the
// success toast carries a one-click Undo (the same bulk-action undo the
// archive flow uses).
//
// Mirrors ContactMoveHouseholdModal's search-and-pick UX, plus a reason
// code (required for the audit/undo trail).

import { useEffect, useState } from "react";

import { api, ApiError, type HouseholdListItem } from "../api";
import { MOVE_REASON_OPTIONS } from "../cleanup/move-reasons";
import { Button } from "../ui";
import { Modal } from "./Modal";
import { useToast } from "./Toast";

export type MovableRecordKind =
  | "service_record"
  | "opportunity"
  | "transaction"
  | "rhythm_instance";

const KIND_NOUN: Record<MovableRecordKind, string> = {
  service_record: "record",
  opportunity: "opportunity",
  transaction: "processing record",
  rhythm_instance: "care plan",
};

// Reason codes that make sense for a household re-home — shared with the
// Cleanup card's confirm step (web/src/cleanup/move-reasons.ts).
const REASON_OPTIONS = MOVE_REASON_OPTIONS;

export interface MoveRecordHouseholdModalProps {
  recordKind: MovableRecordKind;
  recordId: string;
  /** Human label for the record, shown in the copy (e.g. "Home Policy"). */
  recordLabel: string;
  /** The record's current household id — excluded from search results. */
  currentHouseholdId: string | null;
  onClose: () => void;
  /** Fires after a successful move with the destination household id. */
  onMoved: (destinationHouseholdId: string) => void;
}

export function MoveRecordHouseholdModal({
  recordKind,
  recordId,
  recordLabel,
  currentHouseholdId,
  onClose,
  onMoved,
}: MoveRecordHouseholdModalProps) {
  const toast = useToast();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<HouseholdListItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [picked, setPicked] = useState<HouseholdListItem | null>(null);
  const [reasonCode, setReasonCode] = useState<string>("data_quality_cleanup");
  const [reasonText, setReasonText] = useState("");
  const [moving, setMoving] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      setSearching(false);
      setSearchError(null);
      return;
    }
    setSearching(true);
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await api.listHouseholds({ q: query.trim(), limit: 20 });
        if (cancelled) return;
        setResults(res.households.filter((h) => h.id !== currentHouseholdId));
        setSearchError(null);
      } catch (err) {
        if (cancelled) return;
        setSearchError(
          err instanceof ApiError ? `${err.status} — ${err.message}` : "Search failed",
        );
        setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, currentHouseholdId]);

  const reasonTextRequired = reasonCode === "other_with_text";
  const canConfirm =
    picked !== null &&
    !moving &&
    (!reasonTextRequired || reasonText.trim().length > 0);

  async function handleMove() {
    if (!picked || moving) return;
    setMoving(true);
    try {
      const res = await api.moveRecordToHousehold(recordKind, recordId, {
        target_household_id: picked.id,
        reason_code: reasonCode,
        ...(reasonText.trim() ? { reason: reasonText.trim() } : {}),
      });
      toast.success(`Moved to "${picked.display_name}".`, {
        action: {
          label: "Undo",
          onClick: () => {
            void api
              .archiveUndo(res.bulk_action_id)
              .then(() => toast.success("Move undone."))
              .catch(() => toast.error("Undo failed — the record may have changed."));
          },
        },
      });
      onMoved(picked.id);
    } catch (err) {
      toast.error(
        err instanceof ApiError ? `${err.status} — ${err.message}` : "Move failed",
      );
    } finally {
      setMoving(false);
    }
  }

  const noun = KIND_NOUN[recordKind];

  return (
    <Modal
      open={true}
      title={`Move this ${noun} to another household`}
      onClose={moving ? () => undefined : onClose}
      className="modal-wide"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={moving}>
            Cancel
          </Button>
          <Button onClick={() => void handleMove()} disabled={!canConfirm}>
            {moving
              ? "Moving…"
              : picked
                ? `Move to "${picked.display_name}"`
                : "Pick a destination first"}
          </Button>
        </>
      }
    >
      <div
        role="status"
        style={{
          background: "rgba(74, 152, 198, 0.08)",
          border: "1px solid rgba(74, 152, 198, 0.3)",
          borderRadius: 6,
          padding: "10px 12px",
          fontSize: 12,
          marginBottom: 12,
          lineHeight: 1.5,
        }}
      >
        <strong>{recordLabel}</strong> will be re-homed to the household you
        pick. Its own history stays intact, and this is reversible (you'll get
        an Undo).
      </div>

      <label style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
        Search for the destination household
      </label>
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Type at least 2 characters…"
        style={{
          width: "100%",
          padding: "6px 8px",
          border: "1px solid var(--mid-gray)",
          borderRadius: "var(--radius-compact)",
          fontSize: 14,
        }}
        autoFocus
      />

      {searching && (
        <p className="muted" style={{ fontSize: 12, margin: "6px 0 0" }}>
          Searching…
        </p>
      )}
      {searchError && (
        <p style={{ color: "var(--danger)", fontSize: 12, margin: "6px 0 0" }}>
          {searchError}
        </p>
      )}
      {!searching && query.trim().length >= 2 && results.length === 0 && (
        <p className="muted" style={{ fontSize: 12, margin: "6px 0 0" }}>
          No matching households.
        </p>
      )}

      {results.length > 0 && (
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: "8px 0 0",
            maxHeight: 240,
            overflowY: "auto",
            border: "1px solid var(--mid-gray)",
            borderRadius: "var(--radius-compact)",
          }}
        >
          {results.map((h) => {
            const isPicked = picked?.id === h.id;
            return (
              <li key={h.id}>
                <button
                  type="button"
                  onClick={() => setPicked(h)}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    background: isPicked ? "rgba(74, 152, 198, 0.12)" : "transparent",
                    border: "none",
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontSize: 13,
                    borderBottom: "1px solid var(--mid-gray)",
                  }}
                >
                  <div style={{ fontWeight: 600, color: "var(--navy)" }}>
                    {h.display_name}
                  </div>
                  {/* WHO is in this household (Nate 2026-07-14, N17) — the
                      move picker showed only "2 contacts · mortgage,
                      insurance", so three "Klinkhamer Household" rows were
                      indistinguishable (parent vs child). Show the member
                      names, same as the merge picker already does. */}
                  {h.members_label && (
                    <div className="muted" style={{ fontSize: 11.5 }}>
                      {h.members_label}
                    </div>
                  )}
                  <div className="muted" style={{ fontSize: 11 }}>
                    {h.contact_count}{" "}
                    {h.contact_count === 1 ? "contact" : "contacts"}
                    {h.primary_city &&
                      h.primary_state &&
                      ` · ${h.primary_city}, ${h.primary_state}`}
                    {h.company_slugs.length > 0 && ` · ${h.company_slugs.join(", ")}`}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div style={{ marginTop: 12 }}>
        <label
          htmlFor="move-record-reason"
          style={{ display: "block", fontSize: 13, marginBottom: 4 }}
        >
          Reason (recorded for the undo trail)
        </label>
        <select
          id="move-record-reason"
          value={reasonCode}
          onChange={(e) => setReasonCode(e.target.value)}
          disabled={moving}
          style={{
            width: "100%",
            padding: "6px 8px",
            border: "1px solid var(--mid-gray)",
            borderRadius: "var(--radius-compact)",
            fontSize: 14,
          }}
        >
          {REASON_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {reasonTextRequired && (
          <input
            type="text"
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value)}
            disabled={moving}
            placeholder="Briefly, why?"
            style={{
              width: "100%",
              marginTop: 6,
              padding: "6px 8px",
              border: "1px solid var(--mid-gray)",
              borderRadius: "var(--radius-compact)",
              fontSize: 14,
            }}
          />
        )}
      </div>
    </Modal>
  );
}
